import { useCallback, useEffect, useRef } from 'react';
import { ACCOUNT_TYPE_VALUES } from '../../../generated/config-enums';
import { useForm, Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Sparkles, Plus, Link2 } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { DialogForm, LtrToken, BidiText, AIProcessingBanner } from '../../../shared/components/common';
import { useApiError } from '../../../shared/hooks/useApiError';
import {
  CompanyCreateField,
  useCompanyCreatePrecedence,
} from '../../../shared/components/multi-company';
import { Account, AccountCreate, AccountUpdate, AccountType, NormalBalance } from '../types';
import { useAccounts, useAccountNumberSuggestion, useExpenseAccountSuggestion } from '../hooks/useAccounts';
import type {
  ExpenseAccountSuggestion,
  ExpenseAccountSuggestionNewAccount,
  ExpenseAccountSuggestionExistingRef,
} from '../services/accountingApi';

/** Context passed when the form is opened inline from bill import — enables the
 * AI expense-account name/category suggestions seeded from the parsed line. */
export interface AccountSuggestionContext {
  description?: string | null;
  vendorName?: string | null;
  amount?: number | null;
  currencyCode?: string | null;
}

interface AccountFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AccountCreate | AccountUpdate) => Promise<void>;
  account?: Account | null;
  parentAccount?: Account | null;
  isLoading?: boolean;
  /**
   * Seed values for create mode (ignored when editing). Lets a caller pre-fill
   * the form — e.g. the document-import picker opening this as the inline
   * "+ Create" dialog with the parsed line name and an `expense` default type.
   * `normal_balance` follows the seeded `account_type`.
   */
  defaults?: { name?: string; account_type?: AccountType };
  /**
   * When set (create mode, from bill import), the form fetches AI expense-account
   * suggestions seeded from the parsed line and shows name/category chips.
   */
  suggestionContext?: AccountSuggestionContext;
  /**
   * The company the new account must belong to, when the CALLER has already
   * fixed it — the bill import, whose batch captures the company the Bill is
   * booked to. It wins over the app-bar scope (hidden on that route, and "All
   * companies" there leaves nothing to resolve), seeds the create payload, and
   * scopes both the number suggestion and the AI expense-account suggestion —
   * a chart of accounts is per-company, so those calls have no answer without
   * one. Supplying it also hides the company picker: the account has to land in
   * the same chart as the document it is being created for.
   */
  companyId?: string | null;
  /**
   * Called when the user picks an existing account to reuse instead of creating
   * one (the "reuse an existing account" row). The import picker maps this to a
   * match, so no account is created.
   */
  onSelectExisting?: (account: { id: string; name: string }) => void;
}

interface FormData {
  company_id: string;
  account_number: string;
  name: string;
  account_type: AccountType;
  normal_balance: NormalBalance;
  description: string;
  parent_id: string;
  is_header: boolean;
  is_active: boolean;
}

// enum source: shared/config/account_types.config.json — the generated binding,
// so the picker cannot offer a type the CHECK on accounting_accounts rejects.
const ACCOUNT_TYPES: readonly AccountType[] = ACCOUNT_TYPE_VALUES;
const NORMAL_BALANCES: NormalBalance[] = ['debit', 'credit'];

const DEFAULT_NORMAL_BALANCE: Record<AccountType, NormalBalance> = {
  asset: 'debit',
  liability: 'credit',
  equity: 'credit',
  revenue: 'credit',
  direct_cost: 'debit',
  expense: 'debit',
};

export function AccountForm({
  open,
  onClose,
  onSubmit,
  account,
  parentAccount,
  isLoading,
  defaults,
  suggestionContext,
  companyId,
  onSelectExisting,
}: AccountFormProps) {
  const { t } = useTranslation('accounting');
  const { data: accountsData } = useAccounts({ is_header: true });
  const headerAccounts: Account[] = accountsData && 'data' in accountsData
    ? (accountsData.data as Account[])
    : [];

  const isEdit = !!account;
  // Renumbering is allowed only for a non-system account with no posted journal
  // entries (the backend enforces the same guards). A system account or one with
  // history keeps the number field locked, with a reason shown beneath it.
  const renumberLockReason = account?.is_system
    ? t('accounts.numberLockedSystem')
    : account?.has_transactions
      ? t('accounts.numberLockedHasTransactions')
      : null;
  const numberLocked = isEdit && !!renumberLockReason;

  // Company scope (multi-company tenants). `company_id` is set once on create
  // (it isn't part of AccountUpdate) and follows the ONE create-time precedence
  // seam — context > global scope > sole company
  // (docs/platform/multi-company-design.md §3). A caller-supplied `companyId` is
  // the context; a child account inherits its parent's company, since it has to
  // live in the same chart. Context wins outright and renders read-only.
  const contextCompanyId = parentAccount?.company_id ?? companyId ?? null;
  const companyPrecedence = useCompanyCreatePrecedence(contextCompanyId);
  const { defaultCompanyId } = companyPrecedence;

  // The field values a given open should start from — derived from the current
  // account (edit) / parentAccount / defaults (create). Reused for both the
  // initial mount and the reset-on-open effect below so the two never drift.
  const buildFormValues = useCallback(
    (): FormData => ({
      company_id: defaultCompanyId,
      account_number: account?.account_number || '',
      name: account?.name || defaults?.name || '',
      account_type:
        account?.account_type || parentAccount?.account_type || defaults?.account_type || 'asset',
      normal_balance:
        account?.normal_balance ||
        parentAccount?.normal_balance ||
        (defaults?.account_type ? DEFAULT_NORMAL_BALANCE[defaults.account_type] : 'debit'),
      description: account?.description || '',
      parent_id: account?.parent_account_id || parentAccount?.id || '',
      is_header: account?.is_header || false,
      is_active: account?.is_active ?? true,
    }),
    [account, parentAccount, defaults, defaultCompanyId],
  );

  const { control, handleSubmit, watch, setValue, setError, reset, formState: { errors } } =
    useForm<FormData>({ defaultValues: buildFormValues() });

  const watchAccountType = watch('account_type');
  const watchParentId = watch('parent_id');
  const watchNumber = watch('account_number');
  const watchCompanyId = watch('company_id');
  // The company everything downstream is scoped to (number + AI suggestions,
  // create payload): whatever the field holds. It is seeded from
  // `defaultCompanyId` and re-seeded by the effect below once the
  // accessible-company list resolves, so it is only empty in the genuine
  // "All companies, must pick" case.
  const effectiveCompanyId = watchCompanyId;

  // `defaultCompanyId` needs the accessible-company list, so it resolves AFTER
  // the first render. A form mounted already-open — the bill-import inline
  // dialog — never sees the on-open reset below, so without this it would sit
  // company-less and the create would 400. Only fills a blank field, so a user
  // pick (or a context company) is never overwritten.
  useEffect(() => {
    if (isEdit || !open || watchCompanyId || !defaultCompanyId) return;
    setValue('company_id', defaultCompanyId);
  }, [isEdit, open, watchCompanyId, defaultCompanyId, setValue]);

  const handleAccountTypeChange = (type: AccountType) => {
    setValue('account_type', type);
    setValue('normal_balance', DEFAULT_NORMAL_BALANCE[type]);
  };

  // Transient create-mode state that must be cleared on each fresh open (see the
  // reset effect below): whether the user hand-edited the number.
  const numberTouchedRef = useRef(false);

  // The form is permanently mounted on the list page, so react-hook-form reads
  // `defaultValues` only once (at first mount, with no account). Without an
  // explicit reset, each open would carry the previous open's field values AND
  // field-level errors — leaking an edited account into Create, or a cancelled
  // Create (with its stale "number exists" error) into Edit, where a save would
  // silently rename the account. Re-seed values and clear all transient state on
  // every open transition (`reset` also clears the field errors). Declared BEFORE
  // the number-suggestion effect below so it observes the cleared ref on the same
  // open. The AI suggestion needs nothing here: it is a query keyed by the request
  // body, so a re-open re-derives it rather than carrying stale state across.
  // Seeded with the initial `open` so a form that mounts already-open
  // (e.g. the bill-import dialog) keeps its correct mount-time `defaultValues`
  // instead of being reset over — only later open transitions of the always-mounted
  // list-page form reset.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) {
      reset(buildFormValues());
      numberTouchedRef.current = false;
    }
    wasOpen.current = open;
  }, [open, reset, buildFormValues]);

  // --- Smart create: number suggestion + AI expense-account suggestions -----
  // Deterministic next-number + category window for the chosen type/parent.
  // Create mode only, and only once the dialog is open with a resolvable company
  // (the backend requires company_id) — so a closed, always-mounted form on the
  // list page never fires a company_id-less 400.
  const { data: numberSuggestionResp } = useAccountNumberSuggestion(
    {
      account_type: watchAccountType,
      parent_account_id: watchParentId || null,
      company_id: effectiveCompanyId || null,
    },
    !isEdit && open && !!effectiveCompanyId,
  );
  const numberSuggestion = numberSuggestionResp?.data;

  // Once the user edits the number by hand, stop auto-filling it (numberTouchedRef
  // is declared above and reset on each open). `open` is a dependency so the number
  // re-seeds on EVERY open transition: the suggestion query is cached by React
  // Query, so on a re-open with unchanged type/company the `numberSuggestion`
  // reference is stable and would not re-trigger this effect on its own — yet the
  // on-open reset above just blanked the field. Runs after that reset (declared
  // earlier), which clears numberTouchedRef, so the suggested number is restored.
  useEffect(() => {
    if (isEdit || !open) return;
    const suggested = numberSuggestion?.suggested_number;
    if (suggested && !numberTouchedRef.current) {
      setValue('account_number', suggested);
    }
  }, [numberSuggestion, isEdit, setValue, open]);

  // Bill-import create: one AI call for name/category suggestions + reuse hints.
  // A chart of accounts is per-company, so the suggestion is undefined without a
  // company — asking anyway is a guaranteed 400 "company_id is required", which
  // is what an "All companies" scope produced before the company was threaded
  // in. `enabled` withholds the request instead; the render below explains it.
  const seededExpense = (defaults?.account_type ?? 'asset') === 'expense';
  const suggestionDescription = suggestionContext?.description?.trim() ?? '';
  const {
    data: aiSuggestionResp,
    // `isLoading`, not `isFetching`: the banner means "there is nothing to show
    // yet", not "a request is open". A background refresh of an answer we
    // already have must not blank the chips the user is reading.
    isLoading: suggestionLoading,
    isError: suggestionFailed,
  } = useExpenseAccountSuggestion(
    {
      description: suggestionDescription,
      vendor_name: suggestionContext?.vendorName,
      amount: suggestionContext?.amount,
      currency_code: suggestionContext?.currencyCode,
      company_id: effectiveCompanyId,
    },
    !isEdit && open && seededExpense && !!suggestionDescription && !!effectiveCompanyId,
  );
  const aiSuggestion: ExpenseAccountSuggestion | null = aiSuggestionResp?.data ?? null;

  // The category (from the number-suggestion payload) whose range contains a
  // number. `requireParent` restricts to a real existing header account so we
  // only ever set parent_id to something the picker actually offers.
  const categoryForNumber = (value: string | undefined, requireParent: boolean) => {
    const n = Number(value);
    if (!value || Number.isNaN(n)) return undefined;
    return numberSuggestion?.categories?.find(
      (c) => n >= c.range_start && n <= c.range_end && (!requireParent || !!c.parent_account_id),
    );
  };

  // Apply a suggested new account: fill name + number (marking the number as
  // user-set so the deterministic effect won't overwrite it) and, when the
  // account's category maps to an existing header account, preselect it as the
  // parent so the new account lands in the right group.
  const applyNewAccount = (na: ExpenseAccountSuggestionNewAccount) => {
    setValue('name', na.name, { shouldValidate: true });
    if (na.suggested_number) {
      numberTouchedRef.current = true;
      setValue('account_number', na.suggested_number, { shouldValidate: true });
      const category = categoryForNumber(na.suggested_number, true);
      if (category?.parent_account_id) {
        setValue('parent_id', category.parent_account_id);
      }
    }
  };

  const handleUseExisting = (ref: ExpenseAccountSuggestionExistingRef) => {
    onSelectExisting?.({ id: ref.account_id, name: ref.name });
  };

  // Guided bill-import create keeps the number in the operating-expense band; the
  // manual COA-page create (no suggestion context) stays lenient (the backend
  // still enforces band membership, allowing the 9xxx other-expense tail).
  const enforceRange = !isEdit && !!suggestionContext;
  const validateNumberRange = (value: string): true | string => {
    if (!enforceRange || !value) return true;
    const band = numberSuggestion?.band;
    if (!band) return true;
    const n = Number(value);
    if (Number.isNaN(n)) return true; // alphanumeric codes are allowed
    // This message is a plain string handed to react-hook-form (rendered as text,
    // not a component), so <LtrToken> can't wrap the range. Pin the digit–dash–digit
    // run LTR with Unicode isolates (LRI…PDI) instead — the plain-text equivalent —
    // so it doesn't scramble under RTL.
    const range = `\u2066${band.start}–${band.end}\u2069`; // LRI (\u2066) … PDI (\u2069) isolate the range LTR
    return (
      (n >= band.start && n <= band.end) ||
      t('accounts.numberOutOfRange', { range })
    );
  };

  // The create suggestions to offer as chips: the primary recommendation (when
  // it is a create) first, then the alternatives.
  const newAccountSuggestions: { account: ExpenseAccountSuggestionNewAccount; recommended: boolean }[] = [];
  if (aiSuggestion) {
    if (aiSuggestion.recommendation.action === 'create_new') {
      newAccountSuggestions.push({ account: aiSuggestion.recommendation.new_account, recommended: true });
    }
    for (const alt of aiSuggestion.alternatives) {
      newAccountSuggestions.push({ account: alt.new_account, recommended: false });
    }
  }
  // Existing accounts to offer as "reuse instead" chips.
  const reuseSuggestions: ExpenseAccountSuggestionExistingRef[] = [];
  if (aiSuggestion) {
    if (aiSuggestion.recommendation.action === 'use_existing') {
      reuseSuggestions.push(aiSuggestion.recommendation.existing);
    }
    for (const ref of aiSuggestion.close_existing) {
      if (!reuseSuggestions.some((r) => r.account_id === ref.account_id)) {
        reuseSuggestions.push(ref);
      }
    }
  }
  // Why the suggestion panel has nothing to offer, when it has nothing to offer.
  // Three distinct causes, because they are not the same news: `needs_company`
  // is the only one the user can act on (pick a company), `unavailable` is a
  // call that failed, and `none` is a call that SUCCEEDED with nothing to
  // suggest — telling someone their suggestions "didn't load" when the AI
  // simply had no idea is a small lie that sends them looking for a fault.
  // Whichever it is, the panel resolves — the in-progress banner is never
  // terminal.
  const suggestionEmptyReason: 'needs_company' | 'unavailable' | 'none' | null =
    suggestionLoading || newAccountSuggestions.length > 0 || reuseSuggestions.length > 0
      ? null
      : !effectiveCompanyId
        ? 'needs_company'
        : suggestionFailed
          ? 'unavailable'
          : aiSuggestion
            ? 'none'
            : null;
  const showSuggestionArea =
    seededExpense && !isEdit && !!suggestionContext &&
    (suggestionLoading ||
      newAccountSuggestions.length > 0 ||
      reuseSuggestions.length > 0 ||
      suggestionEmptyReason !== null);

  // Field-scoped save errors → red inputs + inline messages (ui-form-errors §3b).
  // showToast:false because the global MutationCache handler already toasts the
  // message once; here we only paint the offending field (e.g. a duplicate
  // ACCOUNT_NUMBER_EXISTS lands inline on the account-number input).
  const { handleMutationError } = useApiError({ showToast: false });

  const handleFormSubmit = async (data: FormData) => {
    // Precedence rule 2's acknowledgement. NOT in the `validate` above:
    // react-hook-form caches a field error until that field re-validates, and
    // confirming the chip changes no value, so the message would outlive the
    // condition. Here it is the field's own, and `confirm()` clears it.
    if (!isEdit && companyPrecedence.blockCreate(data.company_id ?? '')) return;
    if (isEdit) {
      // AccountUpdate is extra='forbid' — send only its fields. account_type /
      // normal_balance are not updatable via this endpoint. account_number is
      // sent ONLY when it actually changed (a renumber): the backend then
      // enforces the eligibility guards (no system account, no posted journal
      // entries, in-band, unique). Re-sending the unchanged number is a no-op
      // there, but omitting it keeps unrelated saves clean.
      const renumbered =
        !!data.account_number && data.account_number !== account?.account_number;
      const updateData: AccountUpdate = {
        name: data.name,
        description: data.description || undefined,
        parent_account_id: data.parent_id || undefined,
        is_header: data.is_header,
        is_active: data.is_active,
        ...(renumbered ? { account_number: data.account_number } : {}),
      };
      try {
        await onSubmit(updateData);
      } catch (error) {
        handleMutationError(error, setError);
      }
      return;
    }

    // AccountCreate is extra='forbid' and has no is_active. Build it explicitly
    // with the correct field names (parent_account_id, not parent_id).
    const createData: AccountCreate = {
      // The caller-supplied company wins outright — its picker is not rendered,
      // so the form field is only a mirror of it.
      company_id: data.company_id || undefined,
      account_number: data.account_number,
      name: data.name,
      account_type: data.account_type,
      normal_balance: data.normal_balance,
      description: data.description || undefined,
      parent_account_id: data.parent_id || undefined,
      is_header: data.is_header,
    };
    try {
      await onSubmit(createData);
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={isEdit ? t('accounts.editAccount') : t('accounts.createAccount')}
      onSubmit={handleSubmit(handleFormSubmit)}
      submitLabel={isEdit ? t('common:app.save') : t('common:app.create')}
      isLoading={isLoading}
      maxWidth="sm"
    >
      <div className="grid grid-cols-12 gap-4">
        {showSuggestionArea && (
          <div className="col-span-12 space-y-3 rounded-md border border-border bg-muted/30 p-3">
            {suggestionLoading ? (
              <AIProcessingBanner message={t('accounts.suggesting')} />
            ) : suggestionEmptyReason ? (
              <p className="text-xs text-muted-foreground">
                {suggestionEmptyReason === 'needs_company'
                  ? t('accounts.suggestionsNeedCompany')
                  : suggestionEmptyReason === 'none'
                    ? t('accounts.suggestionsNone')
                    : t('accounts.suggestionsUnavailable')}
              </p>
            ) : (
              <>
                {newAccountSuggestions.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <Sparkles className="h-3.5 w-3.5 text-module-accent" />
                      {t('accounts.suggestedAccounts')}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {newAccountSuggestions.map((s, i) => (
                        <button
                          key={`new-${i}`}
                          type="button"
                          onClick={() => applyNewAccount(s.account)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <Plus className="h-3 w-3 text-muted-foreground" />
                          {s.account.suggested_number && (
                            <LtrToken className="font-mono text-muted-foreground">
                              {s.account.suggested_number}
                            </LtrToken>
                          )}
                          <BidiText className="font-medium text-foreground">{s.account.name}</BidiText>
                          {s.recommended && (
                            <span className="rounded-sm bg-module-accent/15 px-1 text-[10px] font-medium text-foreground">
                              {t('accounts.recommended')}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {reuseSuggestions.length > 0 && onSelectExisting && (
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">
                      {t('accounts.reuseExisting')}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {reuseSuggestions.map((ref) => (
                        <button
                          key={`reuse-${ref.account_id}`}
                          type="button"
                          onClick={() => handleUseExisting(ref)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <Link2 className="h-3 w-3 text-muted-foreground" />
                          <LtrToken className="font-mono text-muted-foreground">
                            {ref.account_number}
                          </LtrToken>
                          <BidiText className="font-medium text-foreground">{ref.name}</BidiText>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {/* CompanyCreateField owns the whole precedence affordance: a read-only
            chip when the company is context-locked (the account has to land in
            the same chart as the document it is for), a changeable chip for a
            scope-derived pre-fill, the required picker under "All companies",
            and nothing at all in a single-company tenant. */}
        {!isEdit && (
          <div className="col-span-12">
            <Controller
              name="company_id"
              control={control}
              rules={{
                validate: (value) =>
                  !companyPrecedence.multiCompany || !!value
                    ? true
                    : t('common:validation.required'),
              }}
              render={({ field }) => (
                <CompanyCreateField
                  precedence={companyPrecedence}
                  value={field.value}
                  onChange={field.onChange}
                  error={!!errors.company_id}
                  errorMessage={errors.company_id?.message}
                  id="account-company"
                />
              )}
            />
          </div>
        )}

        <div className="col-span-4 space-y-2">
          <Label htmlFor="account_number">{t('accounts.accountNumber')}</Label>
          <Controller
            name="account_number"
            control={control}
            rules={{ required: t('common:validation.required'), validate: validateNumberRange }}
            render={({ field }) => (
              <div>
                <Input
                  {...field}
                  onChange={(e) => {
                    numberTouchedRef.current = true;
                    field.onChange(e);
                  }}
                  id="account_number"
                  disabled={numberLocked}
                  className={errors.account_number ? 'border-destructive' : ''}
                />
                {errors.account_number ? (
                  <p className="text-sm text-destructive mt-1">{errors.account_number.message}</p>
                ) : numberLocked ? (
                  <p className="text-xs text-muted-foreground mt-1">{renumberLockReason}</p>
                ) : !isEdit && numberSuggestion?.range ? (
                  <p className="text-xs text-muted-foreground mt-1">
                    {(() => {
                      const cat = categoryForNumber(watchNumber, false);
                      if (cat) {
                        // Standard sub-category → translated name; a tenant header
                        // account → its own name, bidi-isolated via BidiText. The
                        // digit–dash–digit range is pinned LTR with LtrToken so it
                        // never scrambles under RTL (STYLE_GUIDE Class B).
                        const labelText = cat.category_key
                          ? // Fall back to the backend's English label if a key
                            // ever lacks a translation, never the raw key string.
                            t(`accounts.expenseCategory.${cat.category_key}`, cat.label)
                          : cat.label;
                        return (
                          <Trans
                            i18nKey="accounting:accounts.numberRangeHintCategory"
                            components={{
                              label: cat.category_key ? <span /> : <BidiText />,
                              range: <LtrToken />,
                            }}
                            values={{ label: labelText, range: `${cat.range_start}–${cat.range_end}` }}
                          />
                        );
                      }
                      return (
                        <Trans
                          i18nKey="accounting:accounts.numberRangeHint"
                          components={{ range: <LtrToken /> }}
                          values={{ range: `${numberSuggestion.range.start}–${numberSuggestion.range.end}` }}
                        />
                      );
                    })()}
                  </p>
                ) : null}
              </div>
            )}
          />
        </div>

        <div className="col-span-8 space-y-2">
          <Label htmlFor="name">{t('accounts.name')}</Label>
          <Controller
            name="name"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <div>
                <Input
                  {...field}
                  id="name"
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
                )}
              </div>
            )}
          />
        </div>

        <div className="col-span-6 space-y-2">
          <Label>{t('accounts.accountType')}</Label>
          <Controller
            name="account_type"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) => handleAccountTypeChange(value as AccountType)}
                disabled={isEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`accountTypes.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {/* Type & normal balance are fixed at creation — the update endpoint
              doesn't accept them, so lock them in edit mode rather than silently
              discard a change. */}
          {isEdit && (
            <p className="text-xs text-muted-foreground">{t('accounts.typeLockedOnEdit')}</p>
          )}
        </div>

        <div className="col-span-6 space-y-2">
          <Label>{t('accounts.normalBalance')}</Label>
          <Controller
            name="normal_balance"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NORMAL_BALANCES.map((balance) => (
                    <SelectItem key={balance} value={balance}>
                      {t(`normalBalance.${balance}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="col-span-12 space-y-2">
          <Label>{t('accounts.parentAccount')}</Label>
          <Controller
            name="parent_id"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || 'none'}
                onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                disabled={isEdit && !!account?.has_transactions}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('accounts.noParent')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('accounts.noParent')}</SelectItem>
                  {headerAccounts
                    .filter((a: Account) => a.account_type === watchAccountType && a.id !== account?.id)
                    .map((a: Account) => (
                      <SelectItem key={a.id} value={a.id}>
                        <LtrToken>{a.account_number}</LtrToken> - <BidiText>{a.name}</BidiText>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="col-span-12 space-y-2">
          <Label htmlFor="description">{t('accounts.description')}</Label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="description"
                rows={2}
              />
            )}
          />
        </div>

        <div className="col-span-6 flex items-center space-x-2">
          <Controller
            name="is_header"
            control={control}
            render={({ field }) => (
              <Switch
                id="is_header"
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isEdit && !!account?.has_transactions}
              />
            )}
          />
          <Label htmlFor="is_header">{t('accounts.isHeader')}</Label>
        </div>

        {/* is_active is only part of AccountUpdate — a new account is always
            active, so the toggle is edit-only (avoids a no-op switch on create). */}
        {isEdit && (
          <div className="col-span-6 flex items-center space-x-2">
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <Switch
                  id="is_active"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label htmlFor="is_active">{t('accounts.isActive')}</Label>
          </div>
        )}
      </div>
    </DialogForm>
  );
}
